/**
 * Hook for protecting images with watermark on download/save
 * Adds ROAutoMarket logo watermark when user tries to save/download images
 */

import logoSrc from '@/assets/logo.png';

interface WatermarkOptions {
  logoOpacity?: number;
  logoScale?: number;
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
}

const defaultOptions: WatermarkOptions = {
  logoOpacity: 0.35,
  logoScale: 0.12, // Logo will be 12% of image width
  position: 'bottom-right',
};

/**
 * Adds watermark to an image and returns the watermarked blob
 */
export async function addWatermarkToImage(
  imageUrl: string,
  options: WatermarkOptions = {}
): Promise<Blob> {
  const opts = { ...defaultOptions, ...options };
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Load and draw logo
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      
      logo.onload = () => {
        const logoWidth = canvas.width * (opts.logoScale || 0.12);
        const aspectRatio = logo.naturalHeight / logo.naturalWidth;
        const logoHeight = logoWidth * aspectRatio;
        
        // Calculate position with padding
        const padding = Math.min(canvas.width, canvas.height) * 0.02;
        let x: number, y: number;
        
        switch (opts.position) {
          case 'bottom-left':
            x = padding;
            y = canvas.height - logoHeight - padding;
            break;
          case 'top-right':
            x = canvas.width - logoWidth - padding;
            y = padding;
            break;
          case 'top-left':
            x = padding;
            y = padding;
            break;
          case 'center':
            x = (canvas.width - logoWidth) / 2;
            y = (canvas.height - logoHeight) / 2;
            break;
          case 'bottom-right':
          default:
            x = canvas.width - logoWidth - padding;
            y = canvas.height - logoHeight - padding;
            break;
        }
        
        // Set transparency
        ctx.globalAlpha = opts.logoOpacity || 0.35;
        
        // Add slight shadow-sm for visibility on light backgrounds
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        
        // Draw logo
        ctx.drawImage(logo, x, y, logoWidth, logoHeight);
        
        // Reset alpha
        ctx.globalAlpha = 1;
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create watermarked image'));
            }
          },
          'image/jpeg',
          0.92
        );
      };
      
      logo.onerror = () => {
        // If logo fails, return original image as blob
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create image blob'));
          },
          'image/jpeg',
          0.92
        );
      };
      
      logo.src = logoSrc;
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for watermarking'));
    };
    
    img.src = imageUrl;
  });
}

/**
 * Downloads an image with watermark
 */
export async function downloadWithWatermark(
  imageUrl: string,
  filename: string = 'roautomarket-image.jpg',
  options?: WatermarkOptions
): Promise<void> {
  try {
    const watermarkedBlob = await addWatermarkToImage(imageUrl, options);
    const url = URL.createObjectURL(watermarkedBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error('Error downloading with watermark:', error);
    throw error;
  }
}

/**
 * Creates a watermarked data URL for clipboard/drag operations
 */
export async function getWatermarkedDataUrl(
  imageUrl: string,
  options?: WatermarkOptions
): Promise<string> {
  const blob = await addWatermarkToImage(imageUrl, options);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function useImageProtection() {
  return {
    addWatermarkToImage,
    downloadWithWatermark,
    getWatermarkedDataUrl,
  };
}
